/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import { TooltipDelay } from '@fluentui/react/lib/Tooltip';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { prettyPrintEntity } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { Label } from 'src/modules/labeling/types/Label';

export type TooltipProps = {
    /**
     * Label that the tooltip is attached to.
     */
    label: Label;

    /**
     * DOM target that the tooltip is attached to.
     */
    target: Target;

    /**
     * Children to be rendered inside the tooltip.
     */
    children: React.ReactNode;

    /**
     * Tooltip content to be rendered.
     */
    content?: JSX.Element;
};

const messages = defineMessages({
    entity: {
        id: 'labeling.tooltip.entity',
        defaultMessage: 'Entity:'
    },
    role: {
        id: 'labeling.tooltip.role',
        defaultMessage: 'Role:'
    },
    type: {
        id: 'labeling.tooltip.type',
        defaultMessage: 'Type:'
    },
    prediction: {
        id: 'labeling.tooltip.prediction',
        defaultMessage: 'Prediction:'
    },
    notice: {
        id: 'labeling.tooltip.notice',
        defaultMessage: 'Notice:'
    },
    labelOrConfirm: {
        id: 'labeling.tooltip.labelOrConfirm',
        defaultMessage: 'please label or confirm prediction of its parent first'
    },
    labeled: {
        id: 'labeling.tooltip.labeled',
        defaultMessage: 'Labeled:'
    },
    conflictNotice: {
        id: 'labeling.tooltip.conflictNotice',
        defaultMessage: 'Label and prediction span different tokens'
    },
    predictionErrorLearnMore: {
        id: 'labeling.tooltip.predictionErrorLearnMore',
        defaultMessage: 'Learn more about prediction errors'
    }
});

const { styleDiv, getClassName } = getStylist('LayoutManager');

const { tooltipFontSize } = getScopedTheme('LayoutManager');

const TooltipContainer = styleDiv('TooltipContainer', { padding: 16 });
const labelCellClassName = getClassName('labelCell', theme => ({ paddingRight: 24, color: theme.tooltipLabelColor }));
const cellBottomPaddingClassName = getClassName('cellBottomPadding', { paddingBottom: 6 });
const valueCellClassName = getClassName('valueCell', theme => ({ color: theme.tooltipValueColor }));

/**
 * Tooltip component to be used together with the renderer components.
 */
export const Tooltip = (props: TooltipProps) => {
    const { children, target, label, content } = props;
    const { localize } = useLocalization();

    const renderedContent = content ?? renderTooltipContent(label, localize);

    return (
        <LuisTooltip
            delay={TooltipDelay.long}
            calloutProps={{ target }}
            tooltipProps={{ styles: { root: { padding: 0 } }, maxWidth: '450px', onRenderContent: () => renderedContent }}>
            {children}
        </LuisTooltip>
    );
};

/**
 * Takes an ML label and return its absolute entity path
 * e.g: if the label is C child of B child of A
 * output: "A / B / C"
 */
const getMLEntityLabelsPath = (label: Label) => {
    let currentLabel = label;
    const labelPath = [];

    while (currentLabel) {
        labelPath.unshift(currentLabel);
        currentLabel = currentLabel.parent;
    }

    return labelPath.map(l => l.entity.name).join(' / ');
};

const renderTooltipContent = (label: Label, localize: ReturnType<typeof useLocalization>['localize']) => {
    const renderRole = (role: EntityRole | undefined) => (role ? `:${role.name}` : '');
    const learnMoreLink = label.conflictsWith && (
        <ExternalLink isDarkMode href="https://go.microsoft.com/fwlink/?linkid=2129074">
            {localize(messages.predictionErrorLearnMore)}
        </ExternalLink>
    );

    const defaultContent = (
        <>
            {label.entity.typeId !== ModelType.Simple ? (
                <TooltipTableRow addBottomMargin value={label.entity.name} label={localize(messages.entity)} />
            ) : null}
            <TooltipTableRow
                addBottomMargin={Boolean(label.role)}
                value={label.entity.typeId === ModelType.Simple ? getMLEntityLabelsPath(label) : prettyPrintEntity(label.entity.typeId)}
                label={localize(messages[label.entity.typeId === ModelType.Simple ? 'labeled' : 'type'])}
            />
            {label.role && <TooltipTableRow value={label.role.name} label={localize(messages.role)} />}
        </>
    );

    const predictionContent = (
        <TooltipTableRow value={`${label.entity.name} (${prettyPrintEntity(label.entity.typeId)})`} label={localize(messages.prediction)} />
    );

    const conflictContent = (prediction: Label, userLabel: Label) => {
        const hasConflictNotice =
            userLabel.entity.id === prediction.entity.id &&
            (userLabel.role && prediction.role ? userLabel.role.id === prediction.role.id : true);

        const getLabelName = (l: Label) => (l.entity.typeId === ModelType.Simple ? getMLEntityLabelsPath(l) : l.entity.name);

        return (
            <>
                <TooltipTableRow
                    addBottomMargin
                    value={`${getLabelName(prediction)}${renderRole(prediction.role)}`}
                    label={localize(messages.prediction)}
                />
                <TooltipTableRow
                    addBottomMargin={hasConflictNotice}
                    value={`${getLabelName(userLabel)}${renderRole(userLabel.role)}`}
                    label={localize(messages.labeled)}
                />
                {hasConflictNotice && (
                    <TooltipTableRow addBottomMargin value={localize(messages.conflictNotice)} label={localize(messages.notice)} />
                )}
            </>
        );
    };

    return (
        <TooltipContainer>
            <table>
                <tbody>
                    {(() => {
                        if (label.conflictsWith) {
                            if (label.renderedAs === 'box') {
                                return conflictContent(label, label.conflictsWith);
                            }
                            return conflictContent(label.conflictsWith, label);
                        }
                        if (label.renderedAs === 'box') {
                            return predictionContent;
                        }
                        return defaultContent;
                    })()}
                </tbody>
            </table>
            {learnMoreLink}
        </TooltipContainer>
    );
};

type TooltipTableRowProps = {
    /**
     * Label of the row.
     */
    label: string;

    /**
     * Value of the row
     */
    value: string;

    /**
     * Whether to add bottom margin to the row.
     */
    addBottomMargin?: boolean;
};

/**
 * Represents a tooltip row.
 */
const TooltipTableRow = ({ value, label, addBottomMargin = false }: TooltipTableRowProps) => (
    <tr>
        <td className={`${labelCellClassName} ${addBottomMargin && cellBottomPaddingClassName}`}>
            <LuisText size={tooltipFontSize}>{label}</LuisText>
        </td>

        <td className={valueCellClassName}>
            <LuisText size={tooltipFontSize}>{value}</LuisText>
        </td>
    </tr>
);
